import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ThemeToggle } from '../ThemeToggle';
import { championshipApi } from '../../lib/api';
import {
  LayoutDashboard,
  Trophy,
  Building2,
  Users,
  UserCircle,
  FileCheck,
  FileSearch,
  ClipboardList,
  Medal,
  BarChart3,
  Settings,
  LogOut,
  Menu,
  X,
  Shield,
  Scale,
  MapPin,
  CreditCard,
  History,
  Timer,
  Trash2,
  UserCog,
  MonitorPlay,
  ArrowLeft,
  Database,
  Award,
  ClipboardCheck
} from 'lucide-react';

/**
 * ETAPA VISUAL 2 — MENU EM SEÇÕES
 *
 * Antes: 21 itens numa lista única. Um usuário novo não tinha como saber que
 * "Categorias", "Tempos de Luta" e "Estrutura Evento" são CONFIGURAÇÃO, nem
 * que "Mesa Central" e "Placar" são a OPERAÇÃO do dia do evento.
 *
 * Agora cada item declara a sua `secao`. NENHUM item foi removido e nenhuma
 * `roles` mudou — só ganharam um agrupamento. A ordem dentro de cada seção é a
 * mesma de antes; as seções é que estão em ordem de importância.
 */
const SECOES_GLOBAIS = [
  { id: 'principal', rotulo: 'Principal' },
  { id: 'cadastros', rotulo: 'Cadastros' },
  { id: 'competicao', rotulo: 'Competição' },
  { id: 'operacao', rotulo: 'Operação' },
  { id: 'documentos', rotulo: 'Documentos' },
  { id: 'configuracao', rotulo: 'Configuração' },
  { id: 'administracao', rotulo: 'Administração' },
];

const globalMenuItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard', secao: 'principal', roles: ['super_admin', 'federation', 'professor', 'team'] },
  { icon: Trophy, label: 'Campeonatos', path: '/championships', secao: 'principal', roles: ['super_admin', 'federation', 'professor', 'team', 'staff', 'central_desk', 'athlete'] },
  { icon: Building2, label: 'Federacoes', path: '/federations', secao: 'cadastros', roles: ['super_admin', 'federation'] },
  { icon: Shield, label: 'Equipes', path: '/teams', secao: 'cadastros', roles: ['super_admin', 'federation', 'professor'] },
  { icon: UserCircle, label: 'Atletas', path: '/athletes', secao: 'cadastros', roles: ['super_admin', 'federation', 'professor', 'team'] },
  // Mini gerenciamento de alunos — rotina da equipe (turmas, chamada, mensalidades).
  // `paraMonitor`: o Monitor ativo é `athlete` no banco, então o papel sozinho não
  // abre esta porta — a flag libera o item SÓ com o vínculo ativo vindo do /auth/me.
  { icon: ClipboardCheck, label: 'Minha Equipe', path: '/minha-equipe', secao: 'cadastros', roles: ['super_admin', 'professor', 'team'], paraMonitor: true },
  { icon: CreditCard, label: 'Meu Perfil', path: '/my-profile', secao: 'cadastros', roles: ['athlete', 'professor'] },
  { icon: ClipboardList, label: 'Inscricoes', path: '/registrations', secao: 'competicao', roles: ['super_admin', 'federation', 'professor', 'team'] },
  { icon: Medal, label: 'Medalhas', path: '/medals', secao: 'competicao', roles: ['super_admin', 'federation'] },
  { icon: Award, label: 'Ranking Federativo', path: '/ranking-federativo', secao: 'competicao', roles: ['super_admin', 'federation', 'professor', 'team', 'athlete'] },
  { icon: Users, label: 'Mesa Central', path: '/central-desk', secao: 'operacao', roles: ['super_admin', 'federation', 'staff', 'central_desk'] },
  { icon: MonitorPlay, label: 'Placar', path: '/placar', secao: 'operacao', roles: ['super_admin', 'federation', 'staff', 'central_desk'] },
  { icon: FileCheck, label: 'Meus Documentos', path: '/documentos', secao: 'documentos', roles: ['super_admin', 'federation', 'professor', 'team'] },
  { icon: FileSearch, label: 'Analisar Documentos', path: '/documentos/fila', secao: 'documentos', roles: ['super_admin', 'federation'] },
  { icon: Scale, label: 'Categorias', path: '/weight-categories', secao: 'configuracao', roles: ['super_admin', 'federation'] },
  { icon: Timer, label: 'Tempos de Luta', path: '/fight-times', secao: 'configuracao', roles: ['super_admin', 'federation'] },
  { icon: MapPin, label: 'Estrutura Evento', path: '/event-structure', secao: 'configuracao', roles: ['super_admin', 'federation'] },
  { icon: History, label: 'Auditoria', path: '/audit', secao: 'administracao', roles: ['super_admin', 'federation'] },
  { icon: UserCog, label: 'Usuarios', path: '/admin/users', secao: 'administracao', roles: ['super_admin'] },
  { icon: Trash2, label: 'Resetar Dados', path: '/admin/reset', secao: 'administracao', roles: ['super_admin'] },
  { icon: Database, label: 'Backup/Restauração', path: '/admin/backup', secao: 'administracao', roles: ['super_admin'] },
];

const SECOES_CAMPEONATO = [
  { id: 'evento', rotulo: 'Evento' },
  { id: 'operacao', rotulo: 'Operação' },
];

const championshipMenuItems = [
  { icon: ClipboardList, label: 'Inscricoes', path: 'registrations', secao: 'evento', roles: ['super_admin', 'federation', 'professor', 'team'] },
  { icon: Timer, label: 'Tempos de Luta', path: 'fight-times', secao: 'evento', roles: ['super_admin', 'federation'] },
  { icon: MapPin, label: 'Estrutura do Evento', path: 'event-structure', secao: 'evento', roles: ['super_admin', 'federation'] },
  { icon: Users, label: 'Mesa Central', path: 'central-desk', secao: 'operacao', roles: ['super_admin', 'federation', 'staff', 'central_desk'] },
  { icon: MonitorPlay, label: 'Placar', path: 'placar', secao: 'operacao', roles: ['super_admin', 'federation', 'staff', 'central_desk'] },
  { icon: Medal, label: 'Medalhas', path: 'medals', secao: 'operacao', roles: ['super_admin', 'federation'] },
  { icon: BarChart3, label: 'Ranking', path: 'ranking', secao: 'operacao', roles: ['super_admin', 'federation', 'professor', 'team', 'athlete'] },
];

/** Agrupa os itens visíveis nas seções, na ordem declarada (descarta seção vazia). */
const agruparPorSecao = (itens, secoes) =>
  secoes
    .map((secao) => ({ ...secao, itens: itens.filter((item) => item.secao === secao.id) }))
    .filter((secao) => secao.itens.length > 0);


// Module-level cache for championship data (survives remounts)
const championshipCache = new Map();

/**
 * CORREÇÃO — ROLAGEM DO MENU (guarda de posição)
 *
 * O `MainLayout` é montado DENTRO de cada página, então ao navegar o React
 * desmonta e remonta o `Sidebar` inteiro. O `<nav>` novo nasce com `scrollTop 0`
 * — o menu não "sobe": ele é OUTRO menu (ver comentário no efeito, abaixo).
 *
 * Esta variável vive FORA do componente de propósito, para sobreviver à
 * remontagem. É a correção do SINTOMA; a causa estrutural é resolvida pelo
 * layout de rota em `App.js` + a guarda de contexto no `MainLayout`.
 */
let scrollSalvo = 0;

export function Sidebar() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { user, logout, eMonitor } = useAuth();
  const navigate = useNavigate();
  const [championship, setChampionship] = useState(null);

  /* Devolve a rolagem do menu onde ela estava antes da remontagem.
     `useLayoutEffect` (e não `useEffect`) porque roda ANTES da pintura: sem ele
     o menu apareceria no topo por um quadro e só depois desceria — um piscar. */
  const navRef = useRef(null);

  useLayoutEffect(() => {
    if (navRef.current) navRef.current.scrollTop = scrollSalvo;

    const el = navRef.current;
    return () => {
      if (el) scrollSalvo = el.scrollTop;
    };
  }, []);

  // Detect if we're inside a championship context
  const championshipMatch = location.pathname.match(/^\/championships\/([a-f0-9]{24})/);
  const contextChampionshipId = championshipMatch ? championshipMatch[1] : null;

  // Load championship data when in context (with module-level cache)
  useEffect(() => {
    if (!contextChampionshipId) {
      setChampionship(null);
      return;
    }
    // Serve from cache instantly if available
    if (championshipCache.has(contextChampionshipId)) {
      setChampionship(championshipCache.get(contextChampionshipId));
      return;
    }
    let cancelled = false;
    championshipApi.get(contextChampionshipId)
      .then(({ data }) => {
        if (!cancelled) {
          championshipCache.set(contextChampionshipId, data);
          setChampionship(data);
        }
      })
      .catch(() => { if (!cancelled) setChampionship(null); });
    return () => { cancelled = true; };
  }, [contextChampionshipId]);

  const inChampionshipContext = !!contextChampionshipId;

  /* `paraMonitor` é ADITIVO: nenhuma `roles` mudou e nenhum item de campeonato
     (Mesa Central, Placar, Medalhas, Ranking…) tem essa flag, então eles seguem
     exatamente como antes. */
  const podeVer = (item) =>
    item.roles.includes(user?.role) ||
    user?.role === 'super_admin' ||
    (item.paraMonitor === true && !!eMonitor);

  const secoesGlobais = agruparPorSecao(globalMenuItems.filter(podeVer), SECOES_GLOBAIS);
  const secoesCampeonato = agruparPorSecao(championshipMenuItems.filter(podeVer), SECOES_CAMPEONATO);

  const basePath = `/championships/${contextChampionshipId}`;

  /* O item ativo ganhou a faixa dourada à esquerda (`.nav-item-ativo`) — antes
     era só um fundo dourado a 10%, praticamente invisível no tema escuro. */
  const classeItem = (isActive) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
      isActive ? 'nav-item-ativo' : 'text-[#A1A1AA] hover:text-white hover:bg-[#1A1A1A]'
    }`;

  return (
    <>
      {/* Mobile menu button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-[#141414] border border-[#27272A] text-white"
        data-testid="mobile-menu-btn"
        aria-label={isOpen ? 'Fechar menu' : 'Abrir menu'}
        aria-expanded={isOpen}
      >
        {isOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Overlay */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      {/* ETAPA 8 (densidade) — `w-56` (224px) no notebook, onde os 256px
          consumiam 18,7% da largura da janela; volta a `w-64` a partir de
          `2xl` (1536px), onde há espaço de sobra para os rótulos. */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-56 2xl:w-64 shrink-0 bg-[#141414] border-r border-[#27272A] transform transition-transform duration-300 lg:sticky lg:top-0 lg:h-screen ${
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo / Championship Header */}
          <div className="shrink-0 border-b border-[#27272A] p-4">
            {inChampionshipContext ? (
              <>
                <button
                  onClick={() => { navigate('/championships'); setIsOpen(false); }}
                  className="flex items-center gap-2 text-[#A1A1AA] hover:text-white transition-colors text-sm mb-3"
                >
                  <ArrowLeft size={16} />
                  <span>Campeonatos</span>
                </button>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#D4AF37] to-[#B8860B] flex items-center justify-center flex-shrink-0">
                    <Trophy className="w-5 h-5 text-black" />
                  </div>
                  <div className="min-w-0">
                    <h1 className="font-bold text-sm text-white font-['Chivo'] truncate">
                      {championship?.name || 'Carregando...'}
                    </h1>
                    <p className="text-xs text-[#71717A] truncate">{championship?.date || ''}</p>
                  </div>
                </div>
              </>
            ) : (
              <Link to="/dashboard" className="flex items-center gap-3" data-testid="sidebar-logo">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#D4AF37] to-[#B8860B] flex items-center justify-center">
                  <Trophy className="w-6 h-6 text-black" />
                </div>
                <div>
                  <h1 className="font-bold text-lg text-white font-['Chivo']">BJJ Manager</h1>
                  <p className="text-xs text-[#71717A]">Campeonatos</p>
                </div>
              </Link>
            )}
          </div>

          {/* Navigation — rola SOZINHA quando o menu passa da altura da tela
              (`min-h-0` é o que permite o filho flex encolher e rolar em vez de
              esticar a coluna junto com a página). */}
          <nav ref={navRef} className="flex-1 min-h-0 overflow-y-auto p-3">
            {(inChampionshipContext ? secoesCampeonato : secoesGlobais).map((secao, indiceSecao) => (
              <div key={secao.id} className={indiceSecao > 0 ? 'mt-4' : ''}>
                <p className="micro-rotulo px-3 pb-1.5" data-testid={`secao-${secao.id}`}>
                  {secao.rotulo}
                </p>
                <ul className="space-y-1">
                  {/* No contexto de campeonato, "Detalhes" abre a seção Evento */}
                  {inChampionshipContext && secao.id === 'evento' && (
                    <li>
                      <Link
                        to={basePath}
                        onClick={() => setIsOpen(false)}
                        data-testid="nav-detalhes"
                        aria-current={location.pathname === basePath ? 'page' : undefined}
                        className={classeItem(location.pathname === basePath)}
                      >
                        <LayoutDashboard size={20} />
                        <span className="font-medium">Detalhes</span>
                      </Link>
                    </li>
                  )}

                  {secao.itens.map((item) => {
                    const isActive = inChampionshipContext
                      ? location.pathname === `${basePath}/${item.path}` ||
                        location.pathname.startsWith(`${basePath}/${item.path}/`)
                      : location.pathname === item.path ||
                        location.pathname.startsWith(item.path + '/');
                    return (
                      <li key={item.path}>
                        <Link
                          to={inChampionshipContext ? `${basePath}/${item.path}` : item.path}
                          onClick={() => setIsOpen(false)}
                          data-testid={
                            inChampionshipContext ? `nav-${item.path}` : `nav-${item.path.replace('/', '')}`
                          }
                          aria-current={isActive ? 'page' : undefined}
                          className={classeItem(isActive)}
                        >
                          <item.icon size={20} />
                          <span className="font-medium">{item.label}</span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </nav>

          {/* User section */}
          <div className="shrink-0 border-t border-[#27272A] p-3">
            <div className="flex items-center gap-3 px-3 py-2.5 mb-2">
              <div className="w-10 h-10 rounded-full bg-[#1A1A1A] flex items-center justify-center text-[#D4AF37] font-bold">
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-white truncate">{user?.name}</p>
                <p className="text-xs text-[#71717A] capitalize">{user?.role?.replace('_', ' ')}</p>
              </div>
            </div>
            <div className="px-1 mb-2">
              <ThemeToggle variant="sidebar" />
            </div>
            <button
              onClick={logout}
              data-testid="logout-btn"
              className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-[#A1A1AA] hover:text-red-400 hover:bg-red-400/10 transition-all"
            >
              <LogOut size={20} />
              <span className="font-medium">Sair</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
